// TableBody.tsx - Converted to div-based layout
import { Fragment, useEffect, useState, useRef } from "react";
import Image from "../images/Image";
import useOutsideClick from "../CustomHooks/useOutsideClick";
import { TableDetailsItem, tableMinWidth } from "./tableFunctions";
import moment from "moment";
import CustomModal from "../CustomModal";
import { adminContents } from "../../pages/AdminManagement/components/AdminManagementContent";
import useTranslation from "../CustomHooks/useTranslation";
import { showToast } from "../toastMessage/toastSlice";
import { useDispatch } from "react-redux";
import config from "../../config/config";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import { convertTimestamp } from "../commonfunctions/convertDate";
import ToolTip from "../ToolTip/ToolTip";
import CheckBoxWithLabel from "../checkbox/CheckBoxWithLabel";
import { toTitleCase } from "../commonfunctions/TitleCase";

interface TableBodyProps {
  tableBodyItem: Record<string, any>; // Flexible structure for dynamic keys
  TableDetails: TableDetailsItem; // Generalized structure for table details
  tableBodyIndex: number;
  getList?: () => void;
  handleActions: (id: string, key: string, text?: string) => void;
  hasActivateDeactivateMDPrivilege?: boolean;
  hasViewMDPrivilege?: boolean;
  hasEditMDPrivilege?: boolean;
  mnoList?: boolean;
  listData?: unknown[];
  mainEndPoint?: string;
  advanceStatus?: boolean;
  heightClassName?: string;
  totalRows?: string;
  enableCheckboxes?: boolean;
  selectedIds?: string[];
  onRowSelect?: (id: string, isSelected: boolean) => void;
  allPagesSelected?: boolean;
}

const TableBody = ({
  tableBodyItem,
  TableDetails,
  tableBodyIndex,
  handleActions,
  getList,
  hasActivateDeactivateMDPrivilege,
  hasViewMDPrivilege,
  hasEditMDPrivilege,
  mnoList,
  listData,
  mainEndPoint,
  advanceStatus,
  heightClassName,
  totalRows,
  enableCheckboxes = false,
  selectedIds = [],
  onRowSelect,
  allPagesSelected = false
}: TableBodyProps) => {
  const filterLogs = (logs: any) => {
    if (!logs || typeof logs !== 'object') return logs;
    const filtered: any = Array.isArray(logs) ? [] : {};
    for (const key in logs) {
      const value = logs[key];
      if (value !== null && value !== '' && value !== undefined) {
        const processedValue = typeof value === 'object' ? filterLogs(value) : value;
        const isEmpty = typeof processedValue === 'object' && Object.keys(processedValue).length === 0;
        if (!isEmpty) {
          filtered[key] = processedValue;
        }
      }
    }
    return filtered;
  };
  
  const tableHeaders = Object.entries(TableDetails);
  // Check if this is Money Account Statement (has "split" column)
  const isMoneyAccountStatement = tableHeaders.some(([, details]) => details.id === 'split');
  
  // Check if this table has few columns (3 or fewer non-action columns) and should use full width
  const nonActionColumns = tableHeaders.filter(([, details]) => details.id !== 'actions');
  const shouldUseFullWidth = isMoneyAccountStatement || nonActionColumns.length <= 3;
  const [isOpen, setIsOpen] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const { activeList } = useSelector((state: RootState) => state.activeList);
  const targetStatus = tableBodyItem['target_achieved_status'] === 'Achieved';
  const targetNotStarted = tableBodyItem['target_achieved_status'] === 'Not Started';
  const runningBalance = tableBodyItem.has_transaction === true ? tableBodyItem.has_transaction : tableBodyItem.has_transaction === false ? tableBodyItem.has_transaction : true;
  const tableColumns = tableHeaders.length;
  const dispatch = useDispatch();
  const { translate } = useTranslation(adminContents);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [isFirefox, setIsFirefox] = useState(false);
  const isViewLog = tableHeaders[tableHeaders.length - 1][1].customHeader;
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top' | 'center'>('bottom');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  
  useOutsideClick(dropdownRef, () => setDropdownOpen(false));
  useEffect(() => {
    setIsFirefox(navigator.userAgent.toLowerCase().includes("firefox"));
  }, []);
  const handleStatus = (text?: string) => {
    setButtonLoading(true);
    try {
      handleActions(tableBodyItem.id, "status", text);
    } catch (error: any) {
      dispatch(
        showToast({
          type: "error",
          message: error.message,
        })
      );
    } finally {
      setTimeout(() => {
        setIsOpen(false);
        // if (getList) getList();
        setButtonLoading(false);
      }, 2500);
    }
  };
  const getCustomData = (custom: string[], id?: string) => {
    let customElement = "";
    custom.forEach((item, index = 0) => {
      switch (item) {
        case "commission_percentage":
        case "upfront_commission_received_percentage":
        case "service_fee_percentage":
          if (tableBodyItem[item] && tableBodyItem[item] !== '') {
            customElement = `${Number(tableBodyItem[item]).toFixed(2)}%`;
          } else {
            customElement = "-";
          }
          break;
        case "location":
          if (tableBodyItem.location && tableBodyItem.location !== '') {
            customElement = tableBodyItem.location;
          } else {
            customElement = "-";
          }
          break;
        case "ticket_location":
          if (tableBodyItem.country || tableBodyItem.province || tableBodyItem.region) {
            let locationParts = [];
            if (tableBodyItem.country) locationParts.push(tableBodyItem.country);
            if (tableBodyItem.province) locationParts.push(tableBodyItem.province);
            if (tableBodyItem.region) locationParts.push(tableBodyItem.region);
            customElement = locationParts.join(', ');
          } else {
            customElement = "-";
          }
          break;
        case "profitability_location":
          if (tableBodyItem.country || tableBodyItem.province || tableBodyItem.region) {
            let locationParts = [];
            if (tableBodyItem.region) locationParts.push(tableBodyItem.region);
            if (tableBodyItem.province) locationParts.push(tableBodyItem.province);
            if (tableBodyItem.country) locationParts.push(tableBodyItem.country);
            customElement = locationParts.join(', ');
          } else {
            customElement = "-";
          }
          break;
        case "user_name":
          if (tableBodyItem.name) {
            customElement = `${tableBodyItem.name} ${tableBodyItem.user_code ? `(${tableBodyItem.user_code})` : ''}`;
          } else {
            customElement = "-";
          }
          break;
        case "assignee":
          if (tableBodyItem.assignee_name && tableBodyItem.assignee_name !== '') {
            customElement = `${tableBodyItem.assignee_name} ${tableBodyItem.assignee_user_code && tableBodyItem.assignee_user_code !== '-' ? `(${tableBodyItem.assignee_user_code})` : ''}`;
          } else {
            customElement = "-";
          }
          break;
        case "raised_by":
          if (tableBodyItem.user_name && tableBodyItem.user_name !== '') {
            customElement = `${tableBodyItem.user_name} ${tableBodyItem.user_code && tableBodyItem.user_code !== '-' ? `(${tableBodyItem.user_code})` : ''}`;
          } else {
            customElement = "-";
          }
          break;
        case "credit":
          // Handle direct credit field (e.g., reversal report)
          if (tableBodyItem[item] !== undefined && tableBodyItem[item] !== null && tableBodyItem[item] !== '') {
            customElement = `AFA ${Number(tableBodyItem[item]).toFixed(2)}`;
          } else if (tableBodyItem.transaction_flow === 'cr') {
            // Handle credit based on transaction_flow (e.g., money account statement)
            customElement = `AFA ${Number(tableBodyItem.amount).toFixed(2)}`;
          } else {
            customElement = "-";
          }
          break;
        case "debit":
          // Handle direct debit field (e.g., reversal report)
          if (tableBodyItem[item] !== undefined && tableBodyItem[item] !== null && tableBodyItem[item] !== '') {
            customElement = `AFA ${Number(tableBodyItem[item]).toFixed(2)}`;
          } else if (tableBodyItem.transaction_flow === 'dr') {
            // Handle debit based on transaction_flow (e.g., money account statement)
            customElement = `AFA ${Number(tableBodyItem.amount).toFixed(2)}`;
          } else {
            customElement = "-";
          }
          break;
        case "wallet_type":
          if (tableBodyItem[item]) {
            customElement = tableBodyItem[item] === 'etopup' ? 'eTop Up' : tableBodyItem[item];
          } else {
            customElement = "-";
          }
          break;
        case "flagged":
          customElement = tableBodyItem[item] === true ? 'Yes' : tableBodyItem[item] === false ? 'No' : "-";
          break;
        case "channel":
          if (tableBodyItem[item] && tableBodyItem[item] !== '') {
            customElement = tableBodyItem[item];
          } else {
            customElement = "-";
          }
          break;
        case "source_channel":
          if (tableBodyItem[item] && tableBodyItem[item] !== '') {
            customElement = tableBodyItem[item];
          } else {
            customElement = "-";
          }
          break;
        case "priority":
          if (tableBodyItem[item] && tableBodyItem[item] !== '') {
            customElement = tableBodyItem[item];
          } else {
            customElement = "-";
          }
          break;
        case "payment_type":
          if (tableBodyItem[item]) {
            customElement = tableBodyItem[item].toString().charAt(0).toUpperCase() + tableBodyItem[item].toString().slice(1);
          } else {
            customElement = "-";
          }
          break;

        case "edited_with_compliance":
          if (tableBodyItem[item]) {
            customElement = 'Yes';
          } else {
            customElement = "No";
          }
          break;
        case "date_time":
          if (tableBodyItem.date) {
            customElement = moment(tableBodyItem.date).local().format(
              "DD-MM-YYYY, hh:mm A"
            );
          } else if (tableBodyItem.created_at) {
            customElement = moment(tableBodyItem.created_at).local().format(
              "DD-MM-YYYY, hh:mm A"
            );
          } else {
            customElement = "-";
          }
          break;
        case "purchased_date":
        case "date":
        case "updated_at":
          if (tableBodyItem[item]) {
            customElement = moment(tableBodyItem[item]).local().format(
              "DD-MM-YYYY"
            );
          } else {
            customElement = "-";
          }
          break;

        case "reversal_description":
          if (tableBodyItem.receiver_code) {
            customElement = `Amount reversed to ${tableBodyItem.receiver_code}`;
          } else {
            customElement = "-";
          }
          break;

        case "service_fee_updated_date_time":
        case "reversal_date":
        case "last_logged_in":
        case "action_taken_at":
        case "requested_date":
        case "transaction_date":
        case "last_transaction_date":
        case "flagged_at":
          if (tableBodyItem[item]) {
            customElement = moment(tableBodyItem[item]).local().format(
              "DD-MM-YYYY, hh:mm A"
            );
          } else {
            customElement = "-";
          }
          break;
        case "balance":
          if (tableBodyItem[item]) {
            const value = tableBodyItem[item].toString();
            customElement = value.startsWith('-')
              ? `AFA (-) ${Number(value.substring(1)).toFixed(2)}`
              : `AFA ${Number(value).toFixed(2)}`;
          } else {
            customElement = "-";
          }
          break;
        case "created_at":
          if (tableBodyItem[item]) {
            customElement = convertTimestamp(tableBodyItem[item]);
          } else {
            customElement = "-";
          }
          break;
        case "recharge_sales":
        case "recharge_sales_value":
        case "total_available_balance":
        case "total_service_fee_earned_balance":
        case "purchased_amount":
        case "principal_amount":
        case "advance_amount":
        case "etopup_balance":
        case "bundle_balance":
        case "payable_amount":
        case "stock_purchase_value":
        case "upfront_commission_received_value":
        case "service_fee_received_amount":
        case "running_balance":
        case "recharge_amount":
        case "amount":
          if (tableBodyItem[item]) {
            customElement = `AFA ${Number(tableBodyItem[item]).toFixed(2)}`;
          } else {
            customElement = "-";
          }
          break;
        // A bundle plan can combine several types, rendered as "Voice, SMS & Data".
        case "plan_types":
          if (Array.isArray(tableBodyItem[item]) && tableBodyItem[item].length > 0) {
            const types = tableBodyItem[item];
            customElement =
              types.length === 1
                ? types[0]
                : `${types.slice(0, -1).join(", ")} & ${types[types.length - 1]}`;
          } else {
            customElement = "-";
          }
          break;
        // Bundle plan amounts are shown as plain 2-decimal values, without the
        // AFA prefix, to match the bundle plan listing design.
        case "amount_incl_tax":
        case "amount_excl_tax":
        case "internal_subscription_fee":
          if (tableBodyItem[item] !== null && tableBodyItem[item] !== undefined && tableBodyItem[item] !== "") {
            customElement = Number(tableBodyItem[item]).toFixed(2);
          } else {
            customElement = "-";
          }
          break;
        case "profitability_debit":
          if (tableBodyItem.debit && Number(tableBodyItem.debit) !== 0) {
            customElement = `AFA ${Number(tableBodyItem.debit).toFixed(2)}`;
          } else {
            customElement = "-";
          }
          break;
        case "profitability_credit":
          if (tableBodyItem.credit && Number(tableBodyItem.credit) !== 0) {
            customElement = `AFA ${Number(tableBodyItem.credit).toFixed(2)}`;
          } else {
            customElement = "-";
          }
          break;
        case "onboarded_by_user_details":
          if (tableBodyItem[item]) {
            customElement = `${tableBodyItem[item].first_name} ${tableBodyItem[item].last_name} ${tableBodyItem[item].user_code ? `(${tableBodyItem[item].user_code})` : ''}`;
          } else {
            customElement = "-";
          }
          break;
        case "on_target_service_fee_percentage":
          if (tableBodyItem[item]) {
            customElement = `${Number(tableBodyItem[item]).toFixed(2).replace(/\.00$/, '')}%`;
          } else {
            customElement = "-";
          }
          break;
        case "target_achieved":
        case "achievement_amount_received":
          if (targetStatus && tableBodyItem[item]) {
            customElement = `AFA ${Number(tableBodyItem[item]).toFixed(2)}`;
          } else if (!targetStatus && tableBodyItem[item]) {
            customElement = tableBodyItem[item];
          } else {
            customElement = "-";
          }
          break;
        case "on_target_achievement_amount_received":
          if (tableBodyItem[item] && Number(tableBodyItem[item]) !== 0.00) {
            customElement = `AFA ${Number(tableBodyItem[item]).toFixed(2)}`;
          } else {
            customElement = "-";
          }
          break;
        case "available_balance":
        case "dr_amount":
        case "cr_amount":
        case "statement_balance":
        case "service_fee_received_amount":
        case "service_fee_paid_amount":
        case "target_amount":
          if (tableBodyItem[item] != null && tableBodyItem[item] !== "" && Number(tableBodyItem[item]) !== 0.00) {
            customElement = `AFA ${Number(tableBodyItem[item]).toFixed(2)}`;
          } else {
            customElement = "-";
          }
          break;

        // Not every MNO returns a transaction id of its own (e.g. Etisalat), so
        // blank/missing values fall back to a dash.
        case "mno_transaction_id":
          if (tableBodyItem[item] !== null && tableBodyItem[item] !== undefined && tableBodyItem[item] !== "") {
            customElement = tableBodyItem[item];
          } else {
            customElement = "-";
          }
          break;

        case "custom_status":
          if (tableBodyItem.status) {
            customElement = tableBodyItem.status;
          } else {
            customElement = "-";
          }
          break;

        case "recharged_phone_number":
          if (tableBodyItem[item] !== null) {
            customElement += `${tableBodyItem[item]} `;
          } else {
            customElement = "-";
          }
          break;

        case "commission_type":
          if (tableBodyItem[item] !== null) {
            customElement += `${tableBodyItem[item]} `;
          } else {
            customElement = "-";
          }
          break;
        case "commission_amount":
          if (tableBodyItem.commission_amount) {
            customElement += `AFA ${Number(tableBodyItem.commission_amount).toFixed(2)} `;
          } else {
            customElement = "-";
          }
          break;

        case "description":
          if (tableBodyItem[item] !== null && tableBodyItem[item] !== '') {
            customElement += `${tableBodyItem[item]} `;
          } else {
            customElement = "-";
          }
          break;

        case "first_name":
        case "last_name":
        case "country_code":
        case "cashier_phone_number":
        case "phone_number":
          if (tableBodyItem[item] !== null) {
            customElement += `${tableBodyItem[item]} `;
          }
          break;

        case "province":
        case "district":
        case "country":
          if (!tableBodyItem.province && !tableBodyItem.region && !tableBodyItem.country) {
            customElement = "-";
          } else {
            if (item !== 'district') {
              customElement += `${tableBodyItem[item]}, `;
            } else {
              customElement += `${tableBodyItem[item]}`;
            }
          }
          break;

        default:
          if (tableBodyItem[item] !== null) {
            customElement += `${tableBodyItem[item]}, `;
          }
          if (index === custom.length - 1) {
            customElement = customElement.slice(0, -2);
          }
      }
    });
    return typeof customElement === 'string' ? customElement?.trim() : customElement;
  };

  const handleCustomAction = (key: string, type: string) => {
    switch (key) {
      case 'className':
        if (listData?.[tableBodyIndex]?.status === 'Approved') {
          return 'text-p_primary cursor-pointer'
        } else {
          return 'text-secondary_text'
        }
      case 'action':
        if (listData?.[tableBodyIndex]?.status === 'Approved') {
          return false
        } else {
          return true
        }
      default:
        break;
    }
  }
  const isLastArrayActions = tableHeaders[tableHeaders.length - 1]?.[0] === 'Actions';
  
  // Get the unique identifier for this row
  const rowId = tableBodyItem.id || tableBodyItem.user_code || tableBodyItem.user_id || tableBodyIndex.toString();
  const isRowSelected = allPagesSelected || selectedIds.includes(rowId);
  
  return (
    <>
      {enableCheckboxes && (
        <div className={`text-body2 p-1.5 bg-white border border-[#D8DFE9] w-full flex items-center justify-center ${shouldUseFullWidth ? '' : 'min-w-[60px]'}`}>
          <CheckBoxWithLabel
            label=" "
            checked={isRowSelected}
            handelCheckBox={(_, checked) => onRowSelect?.(rowId, checked)}
            id={`row-checkbox-${tableBodyIndex}`}
          />
        </div>
      )}
      {tableHeaders.map(([key, details], index = 0) => (
        <Fragment key={key}>
          {(details.id === "actions") ? (
            <div className={`text-body2 sticky right-0 bg-white border-t border-b border-l border-r border-[#D8DFE9] ${dropdownOpen ? 'z-[200]' : 'z-[11]'} ${shouldUseFullWidth ? 'w-full' : tableMinWidth(details.id)}`}>
              <div className={`px-2 ${details?.customHeader ? 'py-1.5' : 'py-2'} flex justify-center items-center`}>
                <div className="flex gap-3 items-center justify-center min-w-0">
                  {details?.custom?.includes("text") ? <p
                    className={`z-50 text-large  ${handleCustomAction('className', details?.custom?.includes("text") ? 'text' : '')}`}
                    onClick={() => { handleCustomAction('action', details?.custom?.includes("text") ? 'text' : '') ? {} : handleActions(tableBodyItem.id, details.actionTypes[0], tableBodyItem) }}
                  >{details.actionTypes[0]}</p> : (details.actionTypes.length > 1 && details.isThreeDots) ? (
                    <div className="relative" ref={dropdownRef}>
                      <button
                        ref={buttonRef}
                        onClick={() => {
                          if (!dropdownOpen && buttonRef.current) {
                            if (totalRows === '2' && tableBodyIndex === 0) {
                              setDropdownPosition('center');
                            } else {
                              const rect = buttonRef.current.getBoundingClientRect();
                              const dropdownHeight = details.actionTypes.length * 40 + 8;
                              const tableContainer = buttonRef.current.closest('.scrollBar');
                              if (tableContainer) {
                                const containerRect = tableContainer.getBoundingClientRect();
                                const spaceBelow = containerRect.bottom - rect.bottom;
                                setDropdownPosition((totalRows !== "1" && spaceBelow < dropdownHeight) ? 'top' : 'bottom');
                              } else {
                                const spaceBelow = window.innerHeight - rect.bottom;
                                setDropdownPosition((totalRows !== "1" && spaceBelow < dropdownHeight) ? 'top' : 'bottom');
                              }
                            }
                          }
                          setDropdownOpen(!dropdownOpen);
                        }}
                        className="flex items-center justify-center w-6 h-6 hover:bg-gray-100 rounded cursor-pointer"
                      >
                        <Image src="three-dots" />
                      </button>
                      {dropdownOpen && (
                        <div className={`absolute right-0 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-[300] ${dropdownPosition === 'center' ? 'top-1/2 -translate-y-1/2' : dropdownPosition === 'top' ? 'bottom-full mb-1' : 'mt-1'}`}>
                          {details.actionTypes.map((action: string) => {
                            const isDisabled = action === 'viewLogs' && !tableBodyItem.json_logs;
                            return (
                              <button
                                key={action}
                                onClick={() => {
                                  if (isDisabled) return;
                                  if (action === 'viewLogs') {
                                    setShowLogsModal(true);
                                  } else {
                                    handleActions(tableBodyItem.id, action, tableBodyItem);
                                  }
                                  setDropdownOpen(false);
                                }}
                                className={`w-full text-left px-4 py-2 text-sm  ${isDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100 cursor-pointer'}`}
                                disabled={isDisabled}
                                data-testid={action === 'view' ? 'view-details' : action === 'viewLogs' ? 'view-logs' : toTitleCase(action)}
                              >
                                {action === 'view' ? 'View Details' : action === 'viewLogs' ? 'View Logs' : toTitleCase(action)}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) :
                    (() => {
                      // First, determine which actions are actually visible
                      const visibleActions = details?.actionTypes?.filter((imgItem: string) => {
                        if ((imgItem === "edit" || imgItem === "entry") && targetStatus) return false;
                        if (imgItem === "entry" && targetNotStarted) return false;
                        if (imgItem === "view") return hasViewMDPrivilege;
                        if (imgItem === "edit" || imgItem === 'payment' || imgItem === 'entry') return hasEditMDPrivilege;
                        if (imgItem === "upgrade") return hasEditMDPrivilege && tableBodyItem.credit_status === false;
                        return false;
                      });

                      // Now render each action with position information
                      return details.actionTypes.map((imgItem: string) => {
                        // Check if this action should be visible
                        const isActionVisible =
                          !((imgItem === "edit" || imgItem === "entry") && targetStatus) &&
                          !(imgItem === "entry" && targetNotStarted) &&
                          ((imgItem === "view" && hasViewMDPrivilege) ||
                            ((imgItem === "edit" || imgItem === 'payment' || imgItem === 'entry') && hasEditMDPrivilege) ||
                            (imgItem === "upgrade" && hasEditMDPrivilege && tableBodyItem.credit_status === false));

                        // Find the position of this action in the visible actions array
                        const actionPosition = visibleActions.indexOf(imgItem);

                        // Tooltip position information
                        const tooltipPosition = isActionVisible ? {
                          isFirst: actionPosition === 0,
                          isLast: actionPosition === visibleActions.length - 1,
                          total: visibleActions.length,
                          position: actionPosition + 1,
                          totalActions: details.actionTypes.length,
                        } : undefined;
                        if (details?.customHeader) {
                          const isLogsDisabled = details?.customHeader === 'Logs' && !tableBodyItem.logs;
                          return (
                            <div className="relative group w-full flex items-center justify-center h-8 px-1">
                              <span onClick={details?.actionTypes[0] === 'view' && !isLogsDisabled ? () => setIsOpen(true) : () => { }} className={`${details?.actionTypes[0] === 'view' && !isLogsDisabled ? 'text-p_primary cursor-pointer font-semibold' : details?.actionTypes[0] === 'balance' ? '' : 'text-secondary_text font-semibold cursor-not-allowed'} z-50 text-center`}>{details?.actionTypes[0] === 'view' ? `View ${details?.customHeader.slice(0, -1)}` : 'AFA ' + tableBodyItem?.[details?.customHeader.toLowerCase()]}</span>
                              {details?.actionTypes[0] === 'view' && isLogsDisabled && (
                                <ToolTip top="-top-0.5" left={shouldUseFullWidth ? "right-0 z-[999]" : "-left-[230px] z-[999]"} ToolTipItem="No logs for successfull transaction" />)}
                            </div>
                          );
                        }
                        if ((imgItem === "edit" || imgItem === "entry") && targetStatus || (imgItem === "entry" && targetNotStarted)) {
                          return (
                            <div key={imgItem} className="flex items-center justify-center">
                              <Image
                                testId={`${imgItem}-btn-${tableBodyIndex}`}
                                src={`${imgItem.toLocaleLowerCase()}DisImg`}
                                onClick={() => { }}
                                className={`z-50 cursor-not-allowed`}
                                tableActions={true}
                                index={tableBodyIndex}
                                tooltiptype={imgItem}
                                tooltipPosition={tooltipPosition}
                                tableColumns={tableColumns}
                              />
                            </div>
                          );
                        } else if (imgItem === "view") {
                          // Check if view privilege is false
                          const isViewDisabled = !hasViewMDPrivilege || !runningBalance;
                          
                          return (
                            <div key={imgItem} className="flex items-center justify-center">
                              <Image
                                testId={`${imgItem}-btn-${tableBodyIndex}`}
                                src={isViewDisabled ? `viewDisImg` : `viewImg`}
                                className={`z-50 ${isViewDisabled ? 'cursor-not-allowed' : ''}`}
                                onClick={isViewDisabled ? undefined : () => handleActions(tableBodyItem.id, imgItem, tableBodyItem)}
                                tableActions={true}
                                index={tableBodyIndex}
                                tooltiptype={imgItem}
                                tooltipPosition={tooltipPosition}
                                tableColumns={tableColumns}
                                disabled={isViewDisabled}
                              />
                            </div>
                          );
                        } else if ((imgItem === "edit" || imgItem === 'payment' || imgItem === 'entry') && !advanceStatus) {
                          // Check if edit privilege is false or payment is disabled
                          const isEditDisabled = !hasEditMDPrivilege || (imgItem === 'payment' && Number(tableBodyItem.payable_amount) === 0);
                          
                          return (
                            <div key={imgItem} className="flex items-center justify-center">
                              <Image
                                testId={`${imgItem}-btn-${tableBodyIndex}`}
                                src={isEditDisabled ? `${imgItem.toLocaleLowerCase()}DisImg` : `${imgItem.toLocaleLowerCase()}Img`}
                                className={`z-50 ${isEditDisabled ? 'cursor-not-allowed' : ''}`}
                                onClick={isEditDisabled ? undefined : () => handleActions(tableBodyItem.id, imgItem, tableBodyItem)}
                                tableActions={true}
                                index={tableBodyIndex}
                                tooltiptype={imgItem}
                                tooltipPosition={tooltipPosition}
                                tableColumns={tableColumns}
                                disabled={isEditDisabled}
                              />
                            </div>
                          );
                        } else if (imgItem === "upgrade" && hasEditMDPrivilege) {
                          return (
                            <div key={imgItem} className="flex items-center justify-center">
                              <Image
                                testId={`${imgItem}-btn-${tableBodyIndex}`}
                                src={tableBodyItem.credit_status === true ? 'upgrade-disabledImg' : `${imgItem.toLocaleLowerCase()}Img`}
                                onClick={tableBodyItem.credit_status === false ? () => handleActions(tableBodyItem.id, imgItem) : () => { }}
                                className={`z-50 ${tableBodyItem.credit_status === true ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                tableActions={true}
                                tooltipPosition={tooltipPosition}
                                tableColumns={tableColumns}
                              />
                            </div>
                          );
                        } else if (imgItem === "delete" && hasEditMDPrivilege) {
                          const isLastRecord = totalRows === "1";
                          return (
                            <div key={imgItem} className="flex items-center justify-center">
                              <Image
                                testId={`${imgItem}-btn-${tableBodyIndex}`}
                                src={`${imgItem.toLocaleLowerCase()}Img`}
                                onClick={isLastRecord ? () => { } : () => handleActions(tableBodyIndex.toString(), imgItem, tableBodyItem)}
                                className={`z-50 ${isLastRecord ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                tableActions={true}
                                index={tableBodyIndex}
                                tooltiptype={isLastRecord ? 'Cannot delete last record' : imgItem}
                                tooltipPosition={tooltipPosition}
                              />
                            </div>
                          )
                        }
                        else {
                          // Check if disabled due to missing privileges
                          const isPrivilegeDisabled = 
                            (imgItem === "view" && !hasViewMDPrivilege) ||
                            ((imgItem === "edit" || imgItem === 'payment' || imgItem === 'entry') && !hasEditMDPrivilege);
                          
                          return (
                            <div key={imgItem} className="flex items-center justify-center">
                              <Image
                                testId={`${imgItem}-btn-${tableBodyIndex}`}
                                src={isPrivilegeDisabled ? `${imgItem.toLocaleLowerCase()}DisImg` : `${imgItem.toLocaleLowerCase()}Img`}
                                className="z-50 cursor-not-allowed"
                                tableActions={true}
                                tooltipContent={details.disabledTooltip}
                                tooltiptype={imgItem}
                                disabled={true}
                              />
                            </div>
                          );
                        }
                      });
                    })()
                  }
                </div>
              </div>
            </div>
          ) : (details.id === "status" && !details.custom) ? (
            <div
              className={`text-body2 p-1.5 bg-white border border-[#D8DFE9] w-full overflow-hidden text-ellipsis whitespace-nowrap ${isMoneyAccountStatement ? '' : tableMinWidth(details.id)} ${isMoneyAccountStatement ? '' : details.className || ""}`}
              data-testid={`${details.id}-${tableBodyIndex}`}
            >
              <div className="status custom-toggle flex items-center absolute group">
                {hasActivateDeactivateMDPrivilege && (
                  <label className="switch" data-testid={`${tableBodyItem[details.id] ? `active-${tableBodyIndex}` : `inactive-${tableBodyIndex}`}`}>
                    <input
                      type="checkbox"
                      checked={tableBodyItem[details.id]}
                      onClick={() => setIsOpen(true)}
                    />
                    <span
                      id="user_status"
                      className="slider round"
                      data-testid={`toggle_${tableBodyIndex}`}
                    ></span>
                  </label>
                )}
                <p
                  id="status"
                  className={`disable_switch ${hasActivateDeactivateMDPrivilege ? "ml-3" : ""
                    } -mt-1`}
                >
                  {tableBodyItem[details.id]
                    ? translate("Active")
                    : translate("Inactive")}
                </p>
                <ToolTip
                  ToolTipItem={tableBodyItem[details.id] ? translate("Active") : translate("Inactive")}
                  top="-top-6"
                  left="left-0"
                  width="w-max"
                  bgColor="bg-gray-800"
                  textColor="text-white"
                />
              </div>
            </div>
          ) : details.id === "credit_status" ? (
            <div className={`text-body2 p-1.5 bg-white border border-[#D8DFE9] w-full overflow-hidden ${tableMinWidth(details.id)} `}>
              <div className="flex items-center justify-start group absolute" data-testid={`${details.id}-${tableBodyIndex}`}>
              <p
                id="credit-status"
                className={``}
              >
                {tableBodyItem[details.id]
                  ? translate("Received")
                  : translate("Pending")}
              </p>
              <ToolTip
                ToolTipItem={tableBodyItem[details.id] ? translate("Received") : translate("Pending")}
                top="-top-7"
                left="-left-1"
                width="w-max"
                bgColor="bg-gray-800"
                textColor="text-white"
              />
              </div>
            </div>
          ) : (details.id === "receipt_url" || details.id === "receipt" || details.id === "payment_receipt" || details.id === "transaction_receipt") ? (
            <div className={`text-body2 p-1.5 bg-white border border-[#D8DFE9] w-full overflow-hidden ${tableMinWidth(details.id)}`}>
              <div className="flex items-center justify-start group absolute" data-testid={`${details.id}-${tableBodyIndex}`}>
                
              <p
                id="transaction-reciept"
                className={`flex items-center justify-between w-[160px] break-all`}
              >
                {tableBodyItem[details.id] ? (
                  <div
                    className="cursor-pointer underline flex items-center gap-2 text-p_primary"
                    onClick={() => {
                      const fileUrl = config?.cdnURL + 'public/' + tableBodyItem[details.id];
                      window.open(fileUrl, "_blank");
                    }}
                  >
                    <span>Uploaded Receipt</span>
                    <Image src="link" className="w-5 h-5" />
                  </div>
                ) : (
                  <span>-</span>
                )}
              </p>
              <ToolTip
                ToolTipItem={tableBodyItem[details.id] ? "Uploaded Receipt" : "-"}
                top="-top-7"
                left="-left-1"
                width="w-max"
                bgColor="bg-gray-800"
                textColor="text-white"
              />
              </div>
            </div>
          ) : details.id === "user_activity" ? (
            <div className={`text-body2 p-1.5 bg-white border border-[#D8DFE9] w-full text-ellipsis whitespace-nowrap`}>
              <div className="group relative z-[5]">
                <span className="text-body2">
                  {tableBodyItem[details.id] ?? "-"}
                </span>
                {(tableBodyItem[details.id] === "Active" || 
                  tableBodyItem[details.id] === "Inactive" || 
                  tableBodyItem[details.id] === "Dormant") && (
                    <ToolTip
                      ToolTipItem={
                        tableBodyItem[details.id] === "Active"
                          ? '<span class="font-medium">ACTIVE:</span> User is active within the last 6 days'
                          : tableBodyItem[details.id] === "Inactive"
                          ? '<span class="font-medium">INACTIVE:</span> No activity for 7 to 29 days'
                          : tableBodyItem[details.id] === "Dormant"
                          ? '<span class="font-medium">DORMANT:</span> User not active for 30 or more days'
                          : ""
                      }
                      top={listData?.length === 1 ? "top-[-2px]" : tableBodyIndex === 0 ? "top-[18px]" : "top-[-30px]"}
                      left={listData?.length === 1 ? (tableBodyItem[details.id] === "Dormant" ? "left-[-310px]" : tableBodyItem[details.id] === "Inactive" ? "left-[-250px]" : "left-[-290px]") : "left-[-100px]"}
                      bgColor="bg-[#E5F0FC]"
                      textColor="text-black"
                    />
                )}
              </div>
            </div>
          ) : (
            <div
              className={`${(details.id === 'contract_end_date' && new Date(tableBodyItem.contract_end_date) < new Date()) || (new Date(tableBodyItem.contract_end_date) < new Date() && details.id !== 'actions' && details.id !== 'status') ? 'text-negative text-sm font-normal' : 'text-body2'} ${!isLastArrayActions ? 'p-2.5' : 'p-1.5'} 
                bg-white ${isLastArrayActions && index === tableHeaders.length - 2 ? 'border-l border-y border-[#D8DFE9]' : 'border border-[#D8DFE9]'} ${details.fixed === true && index === tableHeaders.length - 1 ? 'sticky right-0 z-[11]' : ''} w-full min-w-0 text-ellipsis whitespace-nowrap ${isMoneyAccountStatement ? '' : tableMinWidth(details.id)} ${isMoneyAccountStatement ? '' : details.className || ""} group relative`}
           data-testid={`${details.id}-${tableBodyIndex}`}
           >
              {details.link ? <span
                className="text-p_primary underline hover:cursor-pointer block max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
                onClick={() => handleActions(tableBodyIndex.toString(), tableBodyItem, details.id)}
              >
                {details.custom
                  ? getCustomData(details.custom, details.id)
                  : tableBodyItem[details.id] ?? "-"}
              </span> : <span
                /* max-w-full keeps the ellipsis on the text itself: the cell cannot use
                   overflow-hidden because the hover tooltip is positioned above it. */
                className={`block max-w-full overflow-hidden text-ellipsis whitespace-nowrap`}
              >
                {details.custom
                  ? getCustomData(details.custom, details.id)
                  : (details.id === 'contract_start_date' || details.id === 'contract_end_date') && tableBodyItem[details.id]
                    ? new Date(tableBodyItem[details.id]).toLocaleDateString('en-GB').replace(/\//g, '-')
                    : tableBodyItem[details.id] ?? "-"}
              </span>}
              <ToolTip
                ToolTipItem={
                  details.custom
                    ? getCustomData(details.custom, details.id)
                    : (details.id === 'contract_start_date' || details.id === 'contract_end_date') && tableBodyItem[details.id]
                      ? new Date(tableBodyItem[details.id]).toLocaleDateString('en-GB').replace(/\//g, '-')
                      : tableBodyItem[details.id] ?? "-"
                }
                top={details.fixed === true && index === tableHeaders.length - 1 ? "-top-[-6px]" : "-top-6"} 
                left={details.fixed === true && index === tableHeaders.length - 1 ? "-left-[80px]" : "left-0"}
                width="w-max"
                bgColor="bg-gray-800"
                textColor="text-white"
              />
            </div>)}
        </Fragment>
      ))}

      {isOpen && (
        <CustomModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title={isViewLog ? isViewLog : tableBodyItem?.status ? translate("Confirm Deactivation") : translate("Confirm Activation")}
          subText={
            isViewLog ? JSON.stringify(filterLogs(tableBodyItem.logs), null, 2) :
              tableBodyItem?.status
                ? translate("This action will revoke access to all features and tasks")
                : translate("This action will grant access to features and tasks")
          }
          primaryButtonText={tableBodyItem?.status ? translate('Deactivate') : translate("Activate")}
          secondaryButtonText={translate("Cancel")}
          secondaryButton={isViewLog ? false : true}
          handelSubmit={(text) => handleStatus(text)}
          buttonColor={tableBodyItem?.status ? 'bg-negative' : "bg-positive"}
          textArea={isViewLog ? false : tableBodyItem?.status ? true : false}
          buttonLoading={buttonLoading}
          noButtons={isViewLog ? true : false}
          buttonWidth="w-[108px]"
          showCloseButton={isViewLog ? true : false}
        />
      )}
      {showLogsModal && (
        <CustomModal
          isOpen={showLogsModal}
          onClose={() => setShowLogsModal(false)}
          title="Logs"
          subText={JSON.stringify(filterLogs(tableBodyItem.json_logs), null, 2)}
          secondaryButton={false}
          noButtons={true}
          showCloseButton={true}
        />
      )}
    </>
  );
};

export default TableBody;
